import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap'

export interface AddItemTypePayload {
  itemType: string;
  description: string;
  checklistTemplate?: string;
}

interface Props {
  loading: boolean;
  itemType: string;
  description: string;
  onChange: (p: { itemType?: string; description?: string; checklistTemplate?: string }) => void;
  onSave: (payload: AddItemTypePayload) => void;
}

type FieldType = 'text' | 'number' | 'checkbox' | 'date' | 'textarea' | 'select';
interface FieldDef {
  name: string;
  type: FieldType;
  required: boolean;
  unit?: string;
  options?: string[];
  width?: string;
}

type FooterType = 'text' | 'date' | 'line' | 'sign';
interface FooterField { id: string; label: string; type: FooterType; }

interface TableDef {
  id: string;
  title?: string;
  rows: number;
  rowHeight?: string;
  fields: Array<{ id: string; data: FieldDef }>;
  values?: Record<string, any>;
}

type SavedSchemaV2 = {
  version: 2;
  title: string;
  tables: Array<{
    title?: string;
    rows: number;
    rowHeight?: string;
    fields: FieldDef[];
    values?: Record<string, any>;
  }>;
  metaBox: {
    show: boolean;
    fields: string[];
    values?: Record<string, string>;
  };
  detailsRow: {
    show: boolean;
    rightCols: number;
    values?: Record<string, any>;
  };
  footer: {
    columns: number;
    fields: Array<{ label: string; type: FooterType }>;
    values?: Record<string, any>;
  };
};

const uid = () => Math.random().toString(36).slice(2, 9);

// 🔧 CHANGE THIS to your real navbar logo path
const WATERMARK_URL = '/src/assets/KIU Logo (1).png';

const AddItemTypeModal: React.FC<Props> = ({
  loading,
  itemType,
  description,
  onChange,
  onSave,
}) => {
  const [hasTemplate, setHasTemplate] = useState<boolean>(false);
  const [templateJson, setTemplateJson] = useState<string>('');

  // Reset form fields
  const resetForm = () => {
    onChange({ itemType: '', description: '', checklistTemplate: undefined });
    setHasTemplate(false);
    setTemplateJson('');
  };

  // Final create payload
  const finalPayload = useMemo<AddItemTypePayload>(() => {
    const base = { itemType, description };
    if (hasTemplate && templateJson?.trim()) {
      return { ...base, checklistTemplate: templateJson.trim() };
    }
    return base;
  }, [itemType, description, hasTemplate, templateJson]);

  const handleSave = () => {
    if (hasTemplate && !templateJson.trim()) return;

    onSave(finalPayload);

    // deterministically close this modal to ensure hidden.bs.modal fires
    const BS = (window as any).bootstrap;
    const el = document.getElementById('add_item_type');
    if (el && BS?.Modal) {
      BS.Modal.getOrCreateInstance(el).hide();
    }

    resetForm();
  };

  // Integrated Checklist Builder Logic and State
  const initialState = useMemo(() => {
    let title = itemType || 'Maintenance Checklist';
    let tables: TableDef[] = [{
      id: uid(),
      title: '',
      rows: 1,
      fields: [],
      values: {}
    }];
    let metaBox = { show: true, fields: ['Date', 'Inspector'], values: {} };
    let detailsRow = { show: true, rightCols: 0, values: {} };
    let footer = {
      columns: 2,
      fields: [{ label: 'Checked by', type: 'text' as FooterType }, { label: 'Approved by', type: 'text' as FooterType }],
      values: {}
    };

    if (templateJson?.trim()) {
      try {
        const parsed = JSON.parse(templateJson);
        if (parsed?.version === 2) {
          const s = parsed as SavedSchemaV2;
          title = s.title ?? title;
          tables = Array.isArray(s.tables) ? s.tables.map(t => ({
            id: uid(),
            title: t.title,
            rows: Number.isFinite(t.rows) ? t.rows : 14,
            rowHeight: t.rowHeight,
            fields: Array.isArray(t.fields) ? t.fields.map(f => ({ id: uid(), data: { ...f, width: f.width } })) : [],
            values: t.values || {}
          })) : tables;
          metaBox = {
            show: !!s.metaBox?.show,
            fields: Array.isArray(s.metaBox?.fields) ? s.metaBox.fields : metaBox.fields,
            values: s.metaBox?.values || {}
          };
          detailsRow = {
            show: !!s.detailsRow?.show,
            rightCols: 0,
            values: s.detailsRow?.values || {}
          };
          footer = {
            columns: Math.max(1, Math.min(4, s.footer?.columns ?? 2)),
            fields: Array.isArray(s.footer?.fields) ? s.footer.fields : footer.fields,
            values: s.footer?.values || {}
          };
        } else if (Array.isArray(parsed?.fields)) {
          tables[0].fields = parsed.fields.map((f: FieldDef) => ({ id: uid(), data: f }));
        }
      } catch {
        // ignore invalid JSON
      }
    }
    return { title, tables, metaBox, detailsRow, footer };
  }, [templateJson, itemType]);

  const [tables, setTables] = useState<TableDef[]>(initialState.tables);
  const [title, setTitle] = useState<string>(initialState.title);
  const [showMetaBox, setShowMetaBox] = useState<boolean>(initialState.metaBox.show);
  const [metaFields, setMetaFields] = useState<Array<{ id: string; label: string }>>(
    initialState.metaBox.fields.map(l => ({ id: uid(), label: l }))
  );
  const [showDetailsRow, setShowDetailsRow] = useState<boolean>(initialState.detailsRow.show);
  const [footerCols, setFooterCols] = useState<number>(initialState.footer.columns);
  const [footerFields, setFooterFields] = useState<FooterField[]>(
    initialState.footer.fields.map(f => ({ id: uid(), label: f.label, type: f.type }))
  );

  const [entryMode, setEntryMode] = useState<boolean>(true);
  const [tableValues, setTableValues] = useState<Record<string, any>>(
    initialState.tables.reduce((acc, t) => ({ ...acc, ...t.values }), {})
  );
  const [detailsValues, setDetailsValues] = useState<Record<string, any>>(initialState.detailsRow.values);
  const [footerValues, setFooterValues] = useState<Record<string, any>>(initialState.footer.values);

  const addTable = () => {
    setTables(prev => [...prev, { id: uid(), title: '', rows: 14, fields: [] }]);
  };

  const removeTable = (tableId: string) => {
    setTables(prev => prev.filter(t => t.id !== tableId));
    setTableValues(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        if (k.startsWith(`${tableId}:`)) delete next[k];
      });
      return next;
    });
  };

  const updateTableTitle = (tableId: string, title: string) => {
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, title } : t));
  };

  const updateTableRows = (tableId: string, rows: number) => {
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, rows } : t));
  };

  const updateTableRowHeight = (tableId: string, rowHeight: string) => {
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, rowHeight } : t));
  };

  const addColumn = (tableId: string) => {
    setTables(prev => prev.map(t => t.id === tableId ? {
      ...t,
      fields: [...t.fields, { id: uid(), data: { name: `col_${t.fields.length + 1}`, type: 'text', required: false } }]
    } : t));
  };

  const updateColumn = (tableId: string, id: string, patch: Partial<FieldDef>) => {
    setTables(prev => prev.map(t => t.id === tableId ? {
      ...t,
      fields: t.fields.map(c => (c.id === id ? { ...c, data: { ...c.data, ...patch } } : c))
    } : t));
  };

  const removeColumn = (tableId: string, id: string) => {
    setTables(prev => prev.map(t => t.id === tableId ? {
      ...t,
      fields: t.fields.filter(c => c.id !== id)
    } : t));
    setTableValues(prev => {
      const next = { ...prev };
      const colName = tables.find(t => t.id === tableId)?.fields.find(c => c.id === id)?.data.name;
      if (colName) {
        Object.keys(next).forEach(k => {
          if (k.startsWith(`${tableId}:`) && k.endsWith(`:${colName}`)) delete next[k];
        });
      }
      return next;
    });
  };

  const move = (tableId: string, id: string, dir: -1 | 1) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      const i = t.fields.findIndex(c => c.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= t.fields.length) return t;
      const next = [...t.fields];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...t, fields: next };
    }));
  };

  const duplicate = (tableId: string, id: string) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      const i = t.fields.findIndex(c => c.id === id);
      if (i < 0) return t;
      const src = t.fields[i];
      const copy = { id: uid(), data: { ...src.data, name: `${src.data.name}_copy` } };
      const next = [...t.fields];
      next.splice(i + 1, 0, copy);
      return { ...t, fields: next };
    }));
  };

  const addOption = (tableId: string, id: string, raw: string) => {
    const val = raw.trim();
    if (!val) return;
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        fields: t.fields.map(c => {
          if (c.id !== id) return c;
          const opts = c.data.options ?? [];
          if (opts.includes(val)) return c;
          return { ...c, data: { ...c.data, options: [...opts, val] } };
        })
      };
    }));
  };

  const removeOption = (tableId: string, id: string, opt: string) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        fields: t.fields.map(c => {
          if (c.id !== id) return c;
          const next = (c.data.options ?? []).filter(o => o !== opt);
          return { ...c, data: { ...c.data, options: next.length ? next : undefined } };
        })
      };
    }));
  };

  const addMeta = () => setMetaFields(p => [...p, { id: uid(), label: `Field ${p.length + 1}` }]);
  const removeMeta = (id: string) => {
    setMetaFields(p => p.filter(m => m.id !== id));
    setTableValues(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  const addFooter = () => setFooterFields(p => [...p, { id: uid(), label: `Field ${p.length + 1}`, type: 'text' }]);
  const updateFooter = (id: string, patch: Partial<FooterField>) =>
    setFooterFields(p => p.map(f => (f.id === id ? { ...f, ...patch } : f)));
  const removeFooter = (id: string) => {
    setFooterFields(p => p.filter(f => f.id !== id));
    setFooterValues(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const canSaveTemplate = tables.every(t => {
    const names = t.fields.map(c => c.data.name.trim()).filter(Boolean);
    return t.fields.length > 0 && !t.fields.some(c => !c.data.name.trim()) && new Set(names).size === names.length;
  });

  const resetTemplateForm = () => {
    setTables([{ id: uid(), title: '', rows: 14, fields: [], values: {} }]);
    setTitle(itemType || 'Maintenance Checklist');
    setShowMetaBox(true);
    setMetaFields([{ id: uid(), label: 'Date' }, { id: uid(), label: 'Inspector' }]);
    setShowDetailsRow(true);
    setFooterCols(2);
    setFooterFields([
      { id: uid(), label: 'Checked by', type: 'text' },
      { id: uid(), label: 'Approved by', type: 'text' }
    ]);
    setTableValues({});
    setDetailsValues({});
    setFooterValues({});
  };

  const handleSaveTemplate = () => {
    // Build final schema (unchanged structure)
    const schema: SavedSchemaV2 = {
      version: 2,
      title: (title || 'Maintenance Checklist').trim(),
      tables: tables.map(t => ({
        title: t.title?.trim(),
        rows: Math.max(5, Math.min(50, t.rows | 0)),
        rowHeight: t.rowHeight?.trim(),
        fields: t.fields.map(c => {
          const base: FieldDef = {
            name: c.data.name.trim(),
            type: c.data.type,
            required: !!c.data.required,
          };
          if (c.data.type === 'number' && c.data.unit) base.unit = c.data.unit;
          if (c.data.type === 'select' && c.data.options?.length) base.options = c.data.options;
          if (c.data.width?.trim()) base.width = c.data.width.trim();
          return base;
        }),
        // keep only values for this table
        values: Object.keys(tableValues)
          .filter(k => k.startsWith(`${t.id}:`))
          .reduce((acc, k) => ({ ...acc, [k]: tableValues[k] }), {})
      })),
      metaBox: {
        show: !!showMetaBox,
        fields: metaFields.map(m => (m.label || '').trim()).filter(Boolean),
        values: metaFields.reduce((acc, m) => ({ ...acc, [m.label]: tableValues[m.id] || '' }), {})
      },
      detailsRow: {
        show: !!showDetailsRow,
        rightCols: 0,
        values: detailsValues
      },
      footer: {
        columns: Math.max(1, Math.min(4, footerCols | 0)),
        fields: footerFields.map(f => ({ label: (f.label || '').trim(), type: f.type })),
        values: footerValues
      },
    };

    // Set JSON
    setTemplateJson(JSON.stringify(schema));
    onChange({ checklistTemplate: JSON.stringify(schema) });

    // Optional: keep builder state for further edits
  };

  const previewId = `table-preview-add-item-type`;
  const sheetRef = useRef<HTMLDivElement>(null);
  const printPdf = () => window.print();

  useEffect(() => {
    const styleId = `adv-table-style-add-item-type`;
    const existing = document.getElementById(styleId) as HTMLStyleElement | null;
    if (existing) return;

    const s = document.createElement('style');
    s.id = styleId;
    s.innerHTML = `
      .tb-card { border:1px solid #e6e6e6; border-radius:12px; padding:12px; background:#fff; box-shadow:0 1px 1px rgba(0,0,0,.04); }
      .tb-card + .tb-card { margin-top:10px; }
      .tb-btn { padding:6px 10px; border:1px solid #ddd; background:#fafafa; border-radius:8px; }
      .tb-preview-wrap { width:100%; display:flex; justify-content:center; }
      /* A4 exact size preview */
      .tb-a4 { 
        position: relative;
        width: 210mm; 
        height: 297mm; 
        background:#fff; 
        box-shadow:0 0 0.5cm rgba(0,0,0,.2); 
        padding:12mm; 
        margin:0 auto; 
        color:#222;
        overflow:auto;    /* keep inside the page */
      }
      /* Centered watermark (navbar logo) */
      .tb-a4.watermarked::after {
        content:'';
        position:absolute;
        left:50%;
        top:50%;
        transform:translate(-50%, -50%);
        width:70%;
        height:70%;
        background-image:url('${WATERMARK_URL}');
        background-repeat:no-repeat;
        background-position:center center;
        background-size:contain;
        opacity:0.06;             /* soft watermark */
        pointer-events:none;      /* ignore clicks */
        z-index:0;
      }
      /* Lift content above the watermark */
      .tb-a4 > * { position:relative; z-index:1; }

      .tb-head { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:10px; }
      .tb-title { font-weight:700; font-size:18px; }
      .tb-meta { border:1px solid #bbb; border-collapse:collapse; }
      .tb-meta th, .tb-meta td { border:1px solid #bbb; padding:4px 8px; font-size:12px; }
      .tb-meta th { background:#f6f6f6; text-align:left; }
      .tb-table { width:100%; border-collapse:collapse; table-layout: fixed; margin-bottom:12mm; }
      .tb-table th, .tb-table td { border:1px solid #999; padding:6px 8px; font-size:12px; vertical-align:top; }
      .tb-table th { background:#f3f3f3; text-align:left; }
      .tb-input, .tb-select, .tb-textarea { width:100%; border:1px solid #cfd3d8; border-radius:6px; padding:6px 8px; font-size:12px; }
      .tb-textarea { min-height:40px; resize:vertical; }
      .tb-line { height:18px; border-bottom:1px solid #bbb; }
      .tb-bigline { height:40px; border:1px dashed #bbb; border-radius:6px; }
      .tb-box { width:16px; height:16px; border:2px solid #666; border-radius:3px; display:inline-block; vertical-align:middle; }
      .tb-radio { width:14px; height:14px; border:2px solid #666; border-radius:50%; display:inline-block; }
      .tb-footgrid { display:grid; gap:8mm; margin-top:8mm; }
      .tb-foot-item .lbl { font-weight:600; margin-bottom:4px; font-size:12px; }
      .tb-foot-item .sign { height:24mm; border:1px dashed #cbd5e1; border-radius:6px; }
      .tb-foot-item .line { height:18px; border-bottom:1px solid #bbb; }

      @media print {
        @page { size: A4; margin: 12mm; }
        body * { visibility: hidden !important; }
        #${previewId}, #${previewId} * { visibility: visible !important; }
        #${previewId} { position:absolute; left:0; top:0; width:210mm; height:297mm; }
        .modal, .modal-backdrop { display:none !important; }
        .no-print { display:none !important; }
      }
    `;
    document.head.appendChild(s);
  }, [previewId]);

  const cellKey = (tableId: string, r: number, cName: string) => `${tableId}:${r}:${cName}`;

  const renderEntryCell = (tableId: string, f: FieldDef, r: number, cId: string) => {
    const k = cellKey(tableId, r, f.name);
    const v = tableValues[k];
    const set = (val: any) => setTableValues(prev => ({ ...prev, [k]: val }));

    return (
      <>
        {f.type === 'text' && <input className="tb-input" value={v ?? ''} onChange={(e) => set(e.target.value)} />}
        {f.type === 'number' && (
          <div className="d-flex align-items-center gap-2">
            <input
              className="tb-input"
              type="number"
              value={v ?? ''}
              onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))}
              style={{ maxWidth: '120px' }}
            />
            {f.unit && <span className="text-muted">{f.unit}</span>}
          </div>
        )}
        {f.type === 'date' && <input className="tb-input" type="date" value={v ?? ''} onChange={(e) => set(e.target.value)} />}
        {f.type === 'checkbox' && (
          <div className="form-check ms-1">
            <input className="form-check-input" type="checkbox" id={`chk_${k}`} checked={!!v} onChange={(e) => set(e.target.checked)} />
            <label className="form-check-label" htmlFor={`chk_${k}`}>Yes</label>
          </div>
        )}
        {f.type === 'textarea' && <textarea className="tb-textarea" value={v ?? ''} onChange={(e) => set(e.target.value)} />}
        {f.type === 'select' && (
          <select className="tb-select" value={v ?? ''} onChange={(e) => set(e.target.value)}>
            <option value="" disabled>{f.required ? 'Select… (required)' : 'Select…'}</option>
            {(f.options ?? []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        )}
      </>
    );
  };

  const renderDetailsCell = (tableId: string, c: { id: string; data: FieldDef }, ci: number) => {
    const k = `details:${tableId}:${c.data.name}`;
    const v = detailsValues[k];
    const set = (val: any) => setDetailsValues(prev => ({ ...prev, [k]: val }));

    return (
      <td key={`details-${c.id}`} style={{ width: c.data.width }}>
      </td>
    );
  };

  const renderFooterField = (f: FooterField) => {
    const k = f.id;
    const v = footerValues[k];
    const set = (val: any) => setFooterValues(prev => ({ ...prev, [k]: val }));

    return (
      <div className="tb-foot-item" key={f.id}>
        <div className="lbl">{f.label}</div>
        {f.type === 'sign' && <div className="sign" />}
        {f.type === 'line' && <div className="line" />}
        {f.type === 'text' && (entryMode ? <input className="tb-input" value={v ?? ''} onChange={(e) => set(e.target.value)} /> : <div className="line" />)}
        {f.type === 'date' && (entryMode ? <input className="tb-input" type="date" value={v ?? ''} onChange={(e) => set(e.target.value)} /> : <div className="line" />)}
      </div>
    );
  };

  // Builder UI (integrated)
  const renderBuilder = () => (
    <div className="mt-4">
      <h5 className="mb-3">Configure Service Checklist Template</h5>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="form-check">
          <input id="entryModeAdd" className="form-check-input" type="checkbox" checked={entryMode} onChange={(e) => setEntryMode(e.target.checked)} />
          <label className="form-check-label ms-1" htmlFor="entryModeAdd">Entry mode</label>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary btn-sm" onClick={addTable}>
            <i className="ti ti-plus me-1" /> Add Table
          </button>
          <button className="btn btn-success btn-sm" onClick={printPdf} disabled={!tables.some(t => t.fields.length)}>
            <i className="ti ti-printer me-1" /> Download / Print
          </button>
        </div>
      </div>
      <div className="row g-3">
        <div className="col-12 col-lg-6">
          <div className="tb-card">
            <div className="row g-3">
              <div className="col-8">
                <label className="form-label">Document Title</label>
                <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="col-12 d-flex flex-wrap align-items-center gap-3">
                <div className="form-check">
                  <input id="toggleMeta" className="form-check-input" type="checkbox" checked={showMetaBox} onChange={(e) => setShowMetaBox(e.target.checked)} />
                  <label className="form-check-label ms-1" htmlFor="toggleMeta">Show top-right meta box</label>
                </div>
                <div className="form-check">
                  <input id="toggleDetailsRow" className="form-check-input" type="checkbox" checked={showDetailsRow} onChange={(e) => setShowDetailsRow(e.target.checked)} />
                  <label className="form-check-label ms-1" htmlFor="toggleDetailsRow">Show “details row” under header</label>
                </div>
              </div>
            </div>
          </div>

          {showMetaBox && (
            <div className="tb-card mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Meta box (top-right)</div>
                <button className="btn btn-sm btn-outline-primary" onClick={addMeta}>
                  <i className="ti ti-plus me-1" /> Add Meta Field
                </button>
              </div>
              {metaFields.length === 0 && <div className="text-muted">Add meta fields like Date, Inspector, Location…</div>}
              {metaFields.map((m) => (
                <div className="row g-2 align-items-center mb-2" key={m.id}>
                  <div className="col-9">
                    <input
                      className="form-control"
                      value={m.label}
                      onChange={(e) => setMetaFields(p => p.map(x => x.id === m.id ? { ...x, label: e.target.value } : x))}
                      placeholder="Label"
                    />
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <button className="btn btn-outline-danger btn-sm" onClick={() => removeMeta(m.id)}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tables.map((table, tableIdx) => (
            <div className="mt-3" key={table.id}>
              <div className="tb-card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <div className="fw-semibold">Table #{tableIdx + 1}</div>
                    <input
                      className="form-control"
                      style={{ maxWidth: 200 }}
                      value={table.title || ''}
                      onChange={(e) => updateTableTitle(table.id, e.target.value)}
                      placeholder="Table Title (optional)"
                    />
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0">Rows</label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: 80 }}
                        min={1} max={50}
                        value={table.rows}
                        onChange={(e) => updateTableRows(table.id, Math.min(50, Math.max(5, parseInt(e.target.value || '14', 10))))}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0">Row Height</label>
                      <input
                        className="form-control"
                        style={{ width: 100 }}
                        value={table.rowHeight ?? ''}
                        onChange={(e) => updateTableRowHeight(table.id, e.target.value)}
                        placeholder="e.g., 50px"
                      />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-danger btn-sm" onClick={() => removeTable(table.id)} disabled={tables.length === 1}>
                      <i className="ti ti-trash" /> Remove Table
                    </button>
                    <button className="btn btn-outline-primary btn-sm" onClick={() => addColumn(table.id)}>
                      <i className="ti ti-plus me-1" /> Add Column
                    </button>
                  </div>
                </div>
                {!table.fields.length && <div className="alert alert-info">Add columns for the checklist table.</div>}
                {table.fields.some(c => !c.data.name.trim()) && <div className="alert alert-warning py-2">All column names are required.</div>}
                {(() => {
                  const names = table.fields.map(c => c.data.name.trim()).filter(Boolean);
                  return names.length > 0 && new Set(names).size !== names.length && <div className="alert alert-warning py-2">Column names must be unique.</div>;
                })()}

                {table.fields.map((c, idx) => (
                  <div className="tb-card" key={c.id}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-semibold">Column #{idx + 1}</div>
                      <div className="d-flex gap-2">
                        <button className="tb-btn" onClick={() => move(table.id, c.id, -1)} disabled={idx === 0} title="Move up">↑</button>
                        <button className="tb-btn" onClick={() => move(table.id, c.id, 1)} disabled={idx === table.fields.length - 1} title="Move down">↓</button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => duplicate(table.id, c.id)} title="Duplicate">
                          <i className="ti ti-copy" />
                        </button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => removeColumn(table.id, c.id)} title="Remove">
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">Header Name</label>
                        <input
                          className="form-control"
                          value={c.data.name}
                          onChange={(e) => updateColumn(table.id, c.id, { name: e.target.value })}
                          placeholder="e.g., Ref No / Location / Date Tested"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Type</label>
                        <select
                          className="form-select"
                          value={c.data.type}
                          onChange={(e) => {
                            const t = e.target.value as FieldType;
                            const patch: Partial<FieldDef> = { type: t };
                            if (t !== 'number') patch.unit = undefined;
                            if (t !== 'select') patch.options = undefined;
                            updateColumn(table.id, c.id, patch);
                          }}
                        >
                          <option value="text">text</option>
                          <option value="number">number</option>
                          <option value="checkbox">checkbox</option>
                          <option value="date">date</option>
                          <option value="textarea">textarea</option>
                          <option value="select">select</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Width</label>
                        <input
                          className="form-control"
                          value={c.data.width ?? ''}
                          onChange={(e) => updateColumn(table.id, c.id, { width: e.target.value })}
                          placeholder="e.g., 150px or 20%"
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <div className="form-check form-switch">
                          <input className="form-check-input" type="checkbox" checked={c.data.required} onChange={(e) => updateColumn(table.id, c.id, { required: e.target.checked })} id={`req-${c.id}`} />
                          <label className="form-check-label ms-2" htmlFor={`req-${c.id}`}>Required</label>
                        </div>
                      </div>

                      {c.data.type === 'number' && (
                        <div className="col-md-4">
                          <label className="form-label">Unit (optional)</label>
                          <input className="form-control" placeholder="e.g., °C, L, kg" value={c.data.unit ?? ''} onChange={(e) => updateColumn(table.id, c.id, { unit: e.target.value })} />
                        </div>
                      )}

                      {c.data.type === 'select' && (
                        <div className="col-12">
                          <label className="form-label">Options</label>
                          <input
                            className="form-control"
                            placeholder="Type option and press Enter"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addOption(table.id, c.id, (e.target as HTMLInputElement).value);
                                (e.target as HTMLInputElement).value = '';
                              }
                            }}
                          />
                          <div className="mt-2">
                            {(c.data.options ?? []).map((opt) => (
                              <span key={opt} className="badge bg-light text-dark me-1">
                                {opt}{' '}
                                <button className="btn btn-link btn-sm p-0 ms-1" onClick={() => removeOption(table.id, c.id, opt)}>×</button>
                              </span>
                            ))}
                            {!(c.data.options ?? []).length && <div className="text-muted">No options yet.</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="tb-card mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-semibold">Footer (customizable)</div>
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0">Columns</label>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 80 }}
                  min={1} max={4}
                  value={footerCols}
                  onChange={(e) => setFooterCols(Math.max(1, Math.min(4, parseInt(e.target.value || '2', 10))))}
                />
                <button className="btn btn-sm btn-outline-primary" onClick={addFooter}>
                  <i className="ti ti-plus me-1" /> Add Footer Field
                </button>
              </div>
            </div>
            {footerFields.length === 0 && <div className="text-muted">No footer fields yet.</div>}
            {footerFields.map(f => (
              <div className="row g-2 align-items-center mb-2" key={f.id}>
                <div className="col-5">
                  <input className="form-control" value={f.label} onChange={(e) => updateFooter(f.id, { label: e.target.value })} placeholder="Label" />
                </div>
                <div className="col-4">
                  <select className="form-select" value={f.type} onChange={(e) => updateFooter(f.id, { type: e.target.value as FooterType })}>
                    <option value="text">Text (typeable)</option>
                    <option value="date">Date (typeable)</option>
                    <option value="line">Line (print line)</option>
                    <option value="sign">Signature box</option>
                  </select>
                </div>
                <div className="col-3 d-flex justify-content-end">
                  <button className="btn btn-outline-danger btn-sm" onClick={() => removeFooter(f.id)}>
                    <i className="ti ti-trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-12 col-lg-6" id={previewId}>
          <div className="tb-preview-wrap">
            {/* Note: watermarked class adds the centered logo */}
            <div className="tb-a4 watermarked" ref={sheetRef}>
              <div className="tb-head">
                <div>
                  <div className="tb-title">{(title || 'Maintenance Checklist').trim()}</div>
                  <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>
                    Generated: {new Date().toLocaleDateString()}
                  </div>
                </div>

                {showMetaBox && (
                  <table className="tb-meta">
                    <thead><tr><th colSpan={2}>Details</th></tr></thead>
                    <tbody>
                      {metaFields.map((m) => (
                        <tr key={m.id}>
                          <td style={{ minWidth: 90 }}>{m.label}</td>
                          <td style={{ minWidth: 160 }}>
                            {entryMode ? (
                              <input
                                className="tb-input"
                                value={tableValues[m.id] ?? ''}
                                onChange={(e) => setTableValues(prev => ({ ...prev, [m.id]: e.target.value }))}
                              />
                            ) : (
                              <div className="tb-line" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {tables.map((table, tableIdx) => (
                <div key={table.id}>
                  <div className="fw-semibold mb-2">{table.title?.trim() || `Table #${tableIdx + 1}`}</div>
                  <table className="tb-table">
                    <thead>
                      <tr style={{ height: table.rowHeight }}>
                        {table.fields.map((c) => (
                          <th key={c.id} style={{ width: c.data.width }}>
                            {c.data.name || '—'}
                            {c.data.required && <span style={{ color: '#d32f2f', marginLeft: 6 }}>*</span>}
                            {c.data.type === 'number' && c.data.unit && (
                              <span style={{ marginLeft: 6, fontWeight: 400, color: '#666' }}>({c.data.unit})</span>
                            )}
                          </th>
                        ))}
                        {!table.fields.length && <th>Define columns on the left…</th>}
                      </tr>
                    </thead>

                    <tbody>
                      {showDetailsRow && table.fields.length > 0 && (
                        <tr style={{ height: table.rowHeight }}>
                          {table.fields.map((c, ci) => renderDetailsCell(table.id, c, ci))}
                        </tr>
                      )}

                      {Array.from({ length: table.rows }).map((_, r) => (
                        <tr key={`r-${r}`} style={{ height: table.rowHeight }}>
                          {table.fields.map((c) => (
                            <td key={c.id} style={{ width: c.data.width }}>
                              {entryMode ? renderEntryCell(table.id, c.data, r, c.id) : (
                                c.data.type === 'textarea' ? <div className="tb-bigline" /> :
                                c.data.type === 'checkbox' ? <span className="tb-box" /> :
                                c.data.type === 'select' ? (
                                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    {(c.data.options?.slice(0, 4) || ['Option 1', 'Option 2']).map((o) => (
                                      <span key={o} style={{ whiteSpace: 'nowrap' }}>
                                        <span className="tb-radio" /> {o}
                                      </span>
                                    ))}
                                  </div>
                                ) : <div className="tb-line" />
                              )}
                            </td>
                          ))}
                          {!table.fields.length && <td />}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              <div className="tb-footgrid" style={{ gridTemplateColumns: `repeat(${footerCols}, 1fr)` }}>
                {footerFields.map(f => renderFooterField(f))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 text-end">
        <button className="btn btn-success" onClick={handleSaveTemplate} disabled={!canSaveTemplate}>
          Save Template Changes
        </button>
      </div>
    </div>
  );

  return (
    <div className="modal fade" id="add_item_type" role="dialog" aria-hidden="true" >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Item Type</h5>
            <Button
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </Button>
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <label className="col-form-label">Item Type <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={itemType}
                onChange={(e) => onChange({ itemType: e.target.value })}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Description <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={description}
                onChange={(e) => onChange({ description: e.target.value })}
              />
            </div>

            <div className="mb-2 d-flex align-items-center justify-content-between">
              <label className="col-form-label me-3 mb-0">Has service checklist template?</label>
              <div className="form-check form-switch">
                <input
                  id="hasTemplateSwitch"
                  className="form-check-input"
                  type="checkbox"
                  checked={hasTemplate}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setHasTemplate(checked);
                    if (!checked) {
                      setTemplateJson('');
                      onChange({ checklistTemplate: undefined });
                      resetTemplateForm();
                    }
                  }}
                />
                <label className="form-check-label ms-2" htmlFor="hasTemplateSwitch">
                  {hasTemplate ? 'Yes' : 'No'}
                </label>
              </div>
            </div>

            {hasTemplate && (
              <div className="d-flex align-items-center justify-content-between mb-3">
                <small className="text-muted ms-2">
                  {templateJson ? 'Template set' : 'No template yet'}
                </small>
              </div>
            )}

            {hasTemplate && renderBuilder()}
          </div>

          <div className="modal-footer">
            <button className="btn btn-primary me-2" data-bs-dismiss="modal" onClick={resetForm}>
              Cancel
            </button>
            <button
              className="btn btn-success"
              data-bs-dismiss="modal"
              onClick={handleSave}
              disabled={loading || (hasTemplate && !templateJson.trim())}
            >
              {loading ? <span className="spinner-border spinner-border-sm me-2" role="status" /> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItemTypeModal;
